import type { Transaction } from "@rocicorp/zero";
import { userFacingError } from "../../authorization";
import type { Schema } from "../../generatedDrizzleSchema";
import {
  type CrossResourceAvailabilityRuleId,
  crossResourceRule,
  dependentRequirementRank,
  firstUnmetSupplierRequirement,
  type PrincipalKind,
  principalKindOf,
  type RbacReader,
  requiredSupplierPermissions,
  resolveSupplierName,
  zqlGrantReader,
} from "../crossResourceAvailability";
import type { GrantLike } from "../roles";

export type DependentPrincipalGrant = GrantLike & {
  userGroupId: string | null;
  userId: string | null;
  serviceAccountId?: string | null;
};

type ZqlTx = Pick<Transaction<Schema>, "run">;

async function supplierIdsUsedByDependent(
  grantReader: RbacReader,
  ruleId: CrossResourceAvailabilityRuleId,
  dependentId: string
): Promise<Set<string>> {
  const rule = crossResourceRule(ruleId);
  if (!rule.usageEdges) return new Set();
  const edges = await rule.usageEdges(grantReader, { dependentId });
  return new Set(edges.map((e) => e.supplierId));
}

// A stable key for a grant's principal (exactly one of group/user/service
// account is set), or null for a grant with no principal.
export function principalKey(g: {
  userGroupId?: string | null;
  userId?: string | null;
  serviceAccountId?: string | null;
}): string | null {
  return g.userGroupId
    ? `g:${g.userGroupId}`
    : g.userId
      ? `u:${g.userId}`
      : g.serviceAccountId
        ? `s:${g.serviceAccountId}`
        : null;
}

// A downgrade (editor -> viewer) needs no re-validation since use subsumes read.
export function principalsCrossingIntoRequirement(
  ruleId: CrossResourceAvailabilityRuleId,
  existing: readonly DependentPrincipalGrant[],
  desired: readonly DependentPrincipalGrant[]
): DependentPrincipalGrant[] {
  const previousRank = new Map<string, number>();
  for (const row of existing) {
    const key = principalKey(row);
    if (!key) continue;
    const rank = dependentRequirementRank(ruleId, row);
    const current = previousRank.get(key);
    if (current === undefined || rank < current) previousRank.set(key, rank);
  }
  return desired.filter((g) => {
    const key = principalKey(g);
    if (key == null) return false;
    const rank = dependentRequirementRank(ruleId, g);
    if (rank === Number.POSITIVE_INFINITY) return false;
    return rank < (previousRank.get(key) ?? Number.POSITIVE_INFINITY);
  });
}

export async function assertDependentPrincipalsMeetRequirements(
  tx: ZqlTx,
  tenantId: string,
  ruleId: CrossResourceAvailabilityRuleId,
  dependentId: string,
  addedGrants: readonly DependentPrincipalGrant[]
): Promise<void> {
  if (addedGrants.length === 0) return;

  // Only grants whose tier actually requires supplier access need checking.
  const relevant = addedGrants.filter(
    (g) => requiredSupplierPermissions(ruleId, g).length > 0
  );
  if (relevant.length === 0) return;

  const grantReader = zqlGrantReader(tx, tenantId);
  const supplierIds = await supplierIdsUsedByDependent(
    grantReader,
    ruleId,
    dependentId
  );
  if (supplierIds.size === 0) return;

  // Resolve each added user's group memberships once and reuse across every supplier.
  const membershipsByUser = new Map<string, readonly string[]>();
  for (const g of relevant) {
    if (g.userId && !membershipsByUser.has(g.userId)) {
      const userGrants = await grantReader.grantsForPrincipal({
        userId: g.userId,
      });
      membershipsByUser.set(
        g.userId,
        userGrants.map((ug) => ug.scopeId)
      );
    }
  }

  const unmetNames: string[] = [];
  let unmetKind: PrincipalKind | null = null;
  for (const supplierId of supplierIds) {
    const supplierGrants = await grantReader.grantsForScope(supplierId);
    const unmet = firstUnmetSupplierRequirement(
      ruleId,
      relevant,
      supplierGrants,
      membershipsByUser
    );
    if (!unmet) continue;
    unmetKind ??= principalKindOf(unmet.grant);
    unmetNames.push(await resolveSupplierName(tx, ruleId, supplierId));
  }
  if (unmetNames.length > 0) {
    unmetNames.sort();
    throw userFacingError(
      crossResourceRule(ruleId).messages.principalLacksAccess(
        unmetKind!,
        unmetNames
      )
    );
  }
}
